import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useArchivTreffer, useBestellungen } from '@/hooks/useBestellungen';
import { useDashboardSettings } from '@/hooks/useDashboardSettings';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import { DashboardTable } from '@/components/dashboard/DashboardTable';
import { DashboardKarten } from '@/components/dashboard/DashboardKarten';
import { DashboardSearchInput } from '@/components/dashboard/DashboardSearchInput';
import { KatalogHinweis } from '@/components/dashboard/KatalogHinweis';
import { ArchivTrefferGruppe } from '@/components/dashboard/ArchivTrefferGruppe';
import {
  DashboardFilterBar,
  ActiveFilterChips
} from '@/components/dashboard/DashboardFilterBar';
import { ColumnConfigDropdown } from '@/components/dashboard/ColumnConfigDropdown';
import { QuelleTabs } from '@/components/dashboard/QuelleTabs';
import { ScanFenster, type ScanModus } from '@/components/scan/ScanFenster';
import { DEFAULT_VISIBLE_COLUMNS } from '@/lib/dashboardColumns';
import { PackageOpen } from 'lucide-react';
import { clsx } from 'clsx';
import { anzahl } from '@/lib/format';
import { chipIstAktiv, chipSuche, type ChipFilter } from '@/lib/dashboardChips';
import { useIstMobil, useIstSchmal } from '@/hooks/useLayout';
import { useSuchsprung } from '@/hooks/useSuchsprung';
import type { DashboardSearch } from '@/types/dashboard';
import { melde, sucheBegonnen } from '@/lib/nutzung';

const dashboardSearchSchema = z.object({
  // coerce: ?q=9783423282789 kommt sonst als number an und wirft (B2).
  q: z.coerce.string().optional(),
  quelle: z.enum(['alle', 'laden', 'glomer']).optional(),
  zustaende: z.string().optional(),
  erfasser: z.string().optional(),
  mein: z.coerce.boolean().optional(),
  alter_min: z.coerce.number().optional(),
  alter_max: z.coerce.number().optional(),
  verlag_id: z.coerce.number().optional(),
  versandart_id: z.coerce.number().optional(),
  nur_ueberfaellig: z.coerce.boolean().optional(),
  beschaffung_offen: z.coerce.boolean().optional(),
  nachlieferung: z.coerce.boolean().optional(),
  // `include_archiviert` ist mit A10.4 entfallen. Das Archiv hat eine
  // eigene Ansicht (/archiv), und die Suche liefert Archiv-Treffer
  // ohnehin immer als abgesetzte Gruppe mit.

  sort: z.string().optional(),
  // wird vom NewBestellungSheet gesetzt; hier nicht weiter relevant
  neu: z.literal(1).optional()
});

function Dashboard() {
  const navigate = useNavigate();
  const search = Route.useSearch() as DashboardSearch & { neu?: 1 };
  const auth = useAuth();
  const istSchmal = useIstSchmal();
  // Am PC (ab 768 px) stehen Suchfeld und Wareneingang oben in der
  // Leiste (components/layout/KopfWerkzeuge.tsx) — hier nur am Telefon.
  const istMobil = useIstMobil();
  const userId = auth.data?.user.id ?? null;
  const { settings, setVisibleColumns } = useDashboardSettings(userId, {
    visibleColumns: DEFAULT_VISIBLE_COLUMNS
  });

  // Volltext lokal halten, debouncing geschieht im useBestellungen-Hook.
  const [qLocal, setQLocal] = useState(search.q ?? '');
  useEffect(() => {
    setQLocal(search.q ?? '');
  }, [search.q]);

  const list = useBestellungen({ ...search, q: qLocal });

  /**
   * `ersetzen`: kein eigener Verlaufsschritt. Für das Tippen im Suchfeld
   * (Befund 29.10.2026) — sonst war jeder Buchstabe ein Schritt, und
   * „Zurück" lief aus einem Treffer erst durch „Ahrima", „Ahrim" …
   * zurück. Filter und Chips bleiben eigene Schritte: Die setzt man
   * bewusst, und „Zurück" darf sie einzeln zurücknehmen.
   */
  function patchSearch(
    patch: Partial<DashboardSearch>,
    ersetzen = false
  ): void {
    void navigate({
      replace: ersetzen,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      search: ((prev: any) => {
        const next = { ...prev, ...patch };
        // Felder mit undefined entfernen, damit URL sauber bleibt.
        for (const k of Object.keys(next)) {
          if (next[k] === undefined || next[k] === '' || next[k] === false) {
            delete next[k];
          }
        }
        return next;
      }) as never
    });
  }

  /**
   * Ein Chip der Fußzeile ERSETZT die Filterung, statt sie zu ergänzen
   * (A20, #77).
   *
   * Die Zahl auf dem Chip ist ungefiltert gerechnet — sie beschreibt den
   * Arbeitsvorrat des Ladens. Käme sie zu einem bestehenden Filter
   * hinzu, zeigte die Liste danach etwas anderes als die Zahl, auf die
   * man getippt hat. Also: setzen und aufräumen.
   *
   * Was stehen bleibt und warum, steht in lib/dashboardChips.ts.
   */
  function chipSetzen(filter: ChipFilter): void {
    void navigate({ search: (() => chipSuche(search, filter)) as never });
  }

  /**
   * Ist das Scan-Fenster offen, und womit hat es angefangen?
   *
   * Der Modus steht hier nur als STARTWERT: umgeschaltet wird im Fenster
   * selbst, damit die Kamera dabei anbleibt (ScanFenster.tsx). Die Seite
   * merkt sich also nicht, welcher Modus gerade gilt — sie weiß nur,
   * durch welche Tür jemand hineingegangen ist.
   */
  const [scanModus, setScanModus] = useState<ScanModus | null>(null);

  // Was Enter und ein Scan bedeuten — Nummer, Zettel-Adresse, ISBN,
  // Text —, steht seit der oberen Leiste (03.11.2026) in
  // hooks/useSuchsprung.ts: am PC sucht die Leiste, am Telefon dieses
  // Feld, und beide sollen dasselbe tun.
  const { absenden, scanVerarbeiten } = useSuchsprung();

  // WICHTIG (B1): Kein Early-Return mehr fuer Pending/Error. Sonst wird der
  // gesamte Baum inklusive Suchfeld ausgehaengt, sobald sich der Query-Key
  // durch die Eingabe aendert — das Input verliert dabei den Fokus.
  // Nur der Tabellenbereich wechselt; die Toolbar bleibt dauerhaft gemountet.
  const data = list.data;
  const opt = data?.filter_optionen;
  const stats = data?.stats;

  // E12/A10.5: Wer sucht, bekommt Archiv-Treffer immer mit — als
  // abgesetzte Gruppe unter den aktiven. Ohne Suchbegriff laeuft die
  // Abfrage gar nicht erst.
  const archiv = useArchivTreffer(search);

  // Nutzungsmessung: Suche ohne Treffer — weder unter den aktiven noch im
  // Archiv. Erst wenn der Begriff 1,5 Sekunden steht, sonst zählten die
  // Zwischenstände beim Tippen („Ahrim") mit.
  const ohneTreffer =
    qLocal.trim() !== '' &&
    !list.isFetching &&
    !list.isPlaceholderData &&
    list.data?.total === 0 &&
    !archiv.isFetching &&
    (archiv.data?.total ?? 0) === 0;
  useEffect(() => {
    if (!ohneTreffer) return;
    const begriff = qLocal.trim();
    const t = setTimeout(
      () => melde('suche_ohne_treffer', { ort: 'dashboard', text: begriff }),
      1500
    );
    return () => clearTimeout(t);
  }, [ohneTreffer, qLocal]);
  const suchbegriff = (search.q ?? '').trim();

  return (
    <div className="flex h-full flex-col">
      {/* Auf schmalen Geräten bekommt das Suchfeld eine eigene Zeile: es
          ist der meistbenutzte Weg zu einer Bestellung und soll nicht auf
          ein Drittel zusammenschrumpfen, weil daneben noch Reiter stehen.
          Der Kasten bleibt in beiden Fällen über der Liste stehen — die
          Liste scrollt in ihrem eigenen Bereich darunter. */}
      <div
        className={
          istSchmal
            ? 'flex flex-col gap-2 border-b border-line bg-cream px-3 py-2'
            : 'flex flex-wrap items-center gap-2 border-b border-line bg-cream px-4 py-3'
        }
      >
        {/* Am PC steht das Feld oben in der Leiste (KopfWerkzeuge),
            hier nur am Telefon — zwei Suchfelder wären zwei Wahrheiten. */}
        {istMobil && (
          <DashboardSearchInput
            value={qLocal}
            onChange={(v) => {
              setQLocal(v);
              patchSearch({ q: v }, true);
              sucheBegonnen(v);
            }}
            onSubmit={() => void absenden(qLocal)}
            onScanOeffnen={() => setScanModus('suche')}
            isLoading={list.isFetching}
            vollBreite={istSchmal}
          />
        )}
        <div className={istSchmal ? 'flex items-center gap-2' : 'contents'}>
          {/* Eigener Einstieg, nicht der Kameraknopf im Suchfeld.
              Wareneingang buchen ist nicht Suchen — der Knopf im Feld
              steht dort ausdrücklich deshalb, weil Scannen dort dasselbe
              tut wie Tippen. Umschalten geht trotzdem: im Fenster, ohne
              die Kamera neu zu starten. */}
          {istMobil && (
            <button
              data-nutzung="wareneingang"
              type="button"
              data-tour="wareneingang-scan"
              onClick={() => setScanModus('wareneingang')}
              title="Ware scannen und eintragen"
              className="inline-flex min-h-[44px] shrink-0 items-center gap-2 rounded-md border border-line bg-cream px-3 text-sm text-ink-soft hover:border-akzent hover:text-akzent"
            >
              <PackageOpen aria-hidden className="h-4 w-4 shrink-0" />
              <span className={istSchmal ? 'sr-only' : ''}>Wareneingang</span>
            </button>
          )}
          <QuelleTabs
            wert={search.quelle ?? 'alle'}
            onChange={(q) =>
              patchSearch({ quelle: q === 'alle' ? undefined : q })
            }
          />
          <DashboardFilterBar
            anker="dashboard-filter"
            search={search}
            onChange={patchSearch}
            zustaende={opt?.zustaende ?? []}
            erfasser={opt?.erfasser ?? []}
            verlage={opt?.verlage ?? []}
            versandarten={opt?.versandarten ?? []}
          />
          {/* Die Spaltenwahl ordnet eine Tabelle. Wo Karten stehen, ordnet
              sie nichts — der Knopf bliebe ein Knopf ohne Wirkung. */}
          {!istSchmal && (
            <ColumnConfigDropdown
              visible={settings.visibleColumns}
              onChange={setVisibleColumns}
            />
          )}
        </div>
      </div>

      {istMobil && scanModus !== null && (
        <ScanFenster
          startModus={scanModus}
          onSchliessen={() => setScanModus(null)}
          onSuchTreffer={(roh) => void scanVerarbeiten(roh)}
          onGebucht={() => void list.refetch()}
        />
      )}

      <ActiveFilterChips
        search={search}
        onChange={patchSearch}
        zustaende={opt?.zustaende ?? []}
        erfasser={opt?.erfasser ?? []}
        verlage={opt?.verlage ?? []}
        versandarten={opt?.versandarten ?? []}
      />

      {/* K4: Steht im Suchfeld eine Katalognummer, sagt diese Zeile,
          welcher Titel das ist — ZUSÄTZLICH zur Liste darunter, die
          weiterhin die gewöhnlichen Treffer zeigt. */}
      {data?.katalog_hinweis != null && suchbegriff !== '' && (
        <KatalogHinweis treffer={data.katalog_hinweis} />
      )}

      {/* `min-h-0`: ohne das darf dieses Flex-Kind nicht kleiner werden
          als seine eigene Liste — es waere so hoch wie alle Zeilen
          zusammen, und die Fusszeile stuende darunter ausserhalb des
          Bildes. Genau daran lag es, dass sie wegrollte (A10.7). */}
      <div
        className="min-h-0 flex-1 overflow-auto"
        data-scroll-restoration-id="dashboard-liste"
      >
        {list.isError ? (
          <div className="flex flex-col items-center gap-3 p-12 text-center">
            <p className="text-coral">Fehler beim Laden der Bestellungen.</p>
            <Button variant="secondary" onClick={() => void list.refetch()}>
              Erneut versuchen
            </Button>
          </div>
        ) : data === undefined ? (
          <div className="flex items-center justify-center p-12">
            <Spinner className="h-8 w-8" />
          </div>
        ) : (
          <>
            {istSchmal ? (
              <DashboardKarten zeilen={data.bestellungen} />
            ) : (
              <DashboardTable
                zeilen={data.bestellungen}
                visibleColumns={settings.visibleColumns}
                sort={search.sort ?? 'dringlichkeit'}
                onSortChange={(s) =>
                  patchSearch({ sort: s === 'dringlichkeit' ? undefined : s })
                }
              />
            )}
            {/* Gekürzt? Dann sagt es die Liste selbst (Befund 28.10.2026:
                200 von 339 gezeigt, und nirgends stand es — eine neue
                Bestellung schien verschwunden). Übungszeilen zählt die
                Fußzeile nicht mit, deshalb hier auch nicht. */}
            {(() => {
              const echte = data.bestellungen.filter(
                (b) => !b.ist_uebung
              ).length;
              return echte < data.total ? (
                <p role="status" className="px-4 py-3 text-sm text-coral">
                  {echte} von {data.total} Bestellungen angezeigt. Die übrigen
                  findest du über die Suche oder einen Filter.
                </p>
              ) : null;
            })()}
            {suchbegriff !== '' && archiv.data && (
              <ArchivTrefferGruppe
                zeilen={archiv.data.bestellungen}
                total={archiv.data.total}
                suchbegriff={suchbegriff}
                istSchmal={istSchmal}
                visibleColumns={settings.visibleColumns}
              />
            )}
          </>
        )}
      </div>

      {/* Die vier Zahlen sind Filter-Abkürzungen, also Knöpfe — und
          waren 16 Pixel hoch. Mit voller Höhe braucht die Zeile am Telefon
          zwei Reihen; das ist der Preis dafür, dass man sie trifft. */}
      {/* Die Zählzeile.
          ------------------------------------------------------------
          Am Telefon EINE Zeile, die zur Seite geschoben wird — nicht
          zwei, die umbrechen. Die vier Zahlen sind Filter-Abkürzungen
          und müssen deshalb 44 Pixel hoch bleiben; zweizeilig kostete
          das rund 96 Pixel über der Navigationsleiste, also mehr als
          eine Bestellkarte hoch ist.

          Geschoben statt eingeklappt, weil eingeklappt heisst: erst
          tippen, um zu sehen, was los ist. Die Zahlen sind der
          Tagesüberblick — der gehört ins Bild. Mit der Zurück-Wischgeste
          kollidiert das nicht: die gibt es in der installierten App
          nicht, dort gibt es keine Adressleiste.

          Kurze Beschriftungen am Telefon: „mit offener Beschaffung"
          heisst auf den Karten ohnehin schon „offen". */}
      <footer
        className={
          istSchmal
            ? 'flex shrink-0 items-center gap-2 overflow-x-auto border-t border-line bg-cream-warm px-3 py-0.5 text-xs text-ink-soft'
            : // `shrink-0` auch hier (A10.7). Am Telefon stand es laengst;
              // auf dem Desktop fehlte es, und die Fusszeile rutschte mit
              // der Liste aus dem Bild. Jede Zahl darin ist ein Knopf, der
              // einen Filter setzt — weggerollt ist damit nicht nur eine
              // Zahl weg, sondern eine Bedienung.
              'flex shrink-0 flex-wrap items-center gap-x-4 border-t border-line bg-cream-warm px-4 py-1 text-xs text-ink-soft'
        }
      >
        <span className="shrink-0 tabular-nums">
          {istSchmal
            ? (stats?.total ?? 0)
            : anzahl(stats?.total ?? 0, 'Bestellung', 'Bestellungen')}
          {istSchmal && <span className="sr-only"> Bestellungen</span>}
        </span>
        {/* Jeder Chip ist ein SCHALTER: gedrückt zeigt die Liste genau
            diese Zahl, noch einmal gedrückt ist sie wieder ganz da.
            `aria-pressed` und die Fläche sagen, welcher gerade gilt —
            ohne das wäre nicht zu sehen, ob man drin oder draußen ist,
            und ein Schalter ohne sichtbare Stellung ist keiner. */}
        <ZaehlChip
          filter={{ mein: true }}
          aktiv={chipIstAktiv(search, { mein: true })}
          onSetzen={chipSetzen}
          hover="hover:text-akzent"
        >
          {stats?.mein ?? 0} von dir
        </ZaehlChip>
        <ZaehlChip
          filter={{ nur_ueberfaellig: true }}
          aktiv={chipIstAktiv(search, { nur_ueberfaellig: true })}
          onSetzen={chipSetzen}
          hover="hover:text-coral"
        >
          {stats?.ueberfaellig ?? 0} überfällig
        </ZaehlChip>
        <ZaehlChip
          filter={{ beschaffung_offen: true }}
          aktiv={chipIstAktiv(search, { beschaffung_offen: true })}
          onSetzen={chipSetzen}
          hover="hover:text-gold-dark"
        >
          {stats?.beschaffung_offen ?? 0}
          {istSchmal ? ' offen' : ' mit offener Beschaffung'}
        </ZaehlChip>
        {/* E52: nur, wenn es welche gibt (oder der Filter gerade gilt) —
            eine Null hier wäre keine Nachricht. */}
        {((stats?.nachlieferung ?? 0) > 0 || !!search.nachlieferung) && (
          <ZaehlChip
            filter={{ nachlieferung: true }}
            aktiv={chipIstAktiv(search, { nachlieferung: true })}
            onSetzen={chipSetzen}
            hover="hover:text-gold-dark"
          >
            {stats?.nachlieferung ?? 0}
            {istSchmal ? ' nachliefern' : ' Nachlieferung offen'}
          </ZaehlChip>
        )}
        <ZaehlChip
          filter={{ zustaende: 'wartet_auf_kunde' }}
          aktiv={chipIstAktiv(search, { zustaende: 'wartet_auf_kunde' })}
          onSetzen={chipSetzen}
          hover="hover:text-gold-dark"
        >
          {stats?.wartet_auf_kunde ?? 0}
          {istSchmal ? ' wartet' : ' wartet auf Kunde'}
        </ZaehlChip>
      </footer>
    </div>
  );
}

/**
 * Ein Zähler-Chip der Fußzeile.
 *
 * Die Zahl ist ungefiltert gerechnet; ein Tipp macht die Liste zu genau
 * dieser Zahl. Deshalb ist er ein Schalter und kein Zusatzfilter.
 */
function ZaehlChip({
  filter,
  aktiv,
  onSetzen,
  hover,
  children
}: {
  filter: ChipFilter;
  aktiv: boolean;
  onSetzen: (f: ChipFilter) => void;
  hover: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={aktiv}
      onClick={() => onSetzen(filter)}
      className={clsx(
        'inline-flex min-h-[44px] shrink-0 items-center whitespace-nowrap rounded px-2 tabular-nums',
        // Fläche UND Rahmen, nicht nur Farbe — dieselbe Begründung wie
        // an der Seitenleiste (A18.1): ein reiner Farbunterschied
        // verschwindet für alle, die Farben schlecht unterscheiden.
        aktiv ? 'bg-akzent-sanft text-akzent-stark ring-1 ring-akzent' : hover
      )}
    >
      {children}
    </button>
  );
}

export const Route = createFileRoute('/')({
  validateSearch: dashboardSearchSchema.parse,
  component: Dashboard
});
